import { Button, Col, Descriptions, Empty, Result, Row, message } from 'antd'
import { useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import startCase from 'lodash/startCase'
import api from '../../api'
import { useAsync } from '../../hooks'
import datetime, { formatDateTime } from '../../lib/datetime'
import { useIsAdmin } from '../../redux/slices/session'
import { toPath } from '../../routes'
import { ListError, PageHeader } from '../Common'
import PDFPreview from '../Common/PDFPreview'
import Loading from '../Loading'

export default function ShowNotification() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isAdmin = useIsAdmin()

  const getNotification = useCallback(() => api.notifications.get(id), [id])
  const {
    value: notification,
    pending,
    error,
    execute: reloadNotification,
  } = useAsync(getNotification)

  const { pending: isAckPending, execute: ack } = useAsync(async () => {
    try {
      await api.notifications.acknowledge(id)
      await reloadNotification()
      message.success('Notification successfully acknowledged')
    } catch (err) {
      message.error(
        `There was a problem acknowledging this notification: ${err.message}`,
      )
    }
  }, false)

  if (pending) return <Loading large />
  if (error) return <ListError description={error.message} message="Could not load notification" />

  const authorName = notification?.author
    ? startCase(
        [notification.author.firstName, notification.author.lastName]
          .filter(Boolean)
          .join(' '),
      )
    : '-'

  const formattedDate = notification?.createdAt
    ? datetime(notification.createdAt).tz().calendar(null, { sameElse: 'MMM D, YYYY h:mm A' })
    : '-'

  const acknowledgedSubtitle =
    notification?.acknowledgedAt &&
    `Acknowledged ${formatDateTime(notification.acknowledgedAt)}${
      notification.acknowledger
        ? ` by ${notification.acknowledger.firstName} ${notification.acknowledger.lastName}`
        : ''
    }`

  return (
    <div>
      <PageHeader
        title={notification?.title || 'Notification'}
        subTitle={acknowledgedSubtitle}
        onBack={() => navigate(-1)}
      />

      {notification?.acknowledgedAt || isAdmin ? (
        <>
          <Row gutter={24} style={{ marginBottom: 24 }}>
            <Col span={24}>
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="Type">
                  {startCase(notification?.notificationType)}
                </Descriptions.Item>
                <Descriptions.Item label="Sub-type">
                  {startCase(notification?.subType)}
                </Descriptions.Item>
                <Descriptions.Item label="Patient">
                  {notification?.patient ? (
                    <Link to={toPath.patients.patient({ id: notification.patient.id })}>
                      {notification.patient.lastName}, {notification.patient.firstName}
                    </Link>
                  ) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Sent by">{authorName}</Descriptions.Item>
                <Descriptions.Item label="Device Model">
                  {notification?.patient?.deviceModel || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Serial Number">
                  {notification?.patient?.serialNumber || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Date" span={2}>{formattedDate}</Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>

          {notification?.filePath ? (
            <div style={{ width: '70%', margin: '0 auto' }}>
              <PDFPreview path={notification.filePath} showAllPages />
            </div>
          ) : (
            <Empty description="No PDF available for this notification" />
          )}
        </>
      ) : (
        <Result
          title="You need to acknowledge this notification before you can see its contents"
          extra={
            <Button type="primary" loading={isAckPending} onClick={ack}>
              Acknowledge
            </Button>
          }
        />
      )}
    </div>
  )
}
